import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { IUserUpdate } from "src/app/models/userUpdate";
import { IDirectoryUser } from "src/app/models/directory/users/DirectoryUser";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { PageLayout } from "@cvx/nextpage";
import { SelectionModel } from "@angular/cdk/collections";
import { ResultSizeConstants } from "src/app/models/common/enums/ResultSizeConstants.enum";
import { GraphUserService } from "src/app/services/graph/GraphUser.service";
import { CalAngularService } from "@cvx/cal-angular";
import { IGenericApiResponseWithWorkflowRequest } from "src/app/models/common/GenericApiResponseWithWorkflowRequest";
import { GuestUser, UpdateGuestUserRequest } from "src/app/models/guestAccounts/UpdateGuestUserRequest";
import { CocoUser, UpdateCocoUserRequest } from "src/app/models/cocoUser/UpdateCocoUserRequest";
import { FormService } from "src/app/services/form.service";
import { ServiceAccount, UpdateServiceAccountRequest } from "src/app/models/servicePrivilegeAccounts/UpdateServiceAccountRequest";
import { matTableSelectionHelper } from "src/app/helpers/matTableSelectionHelper";
import { UntypedFormControl, Validators } from "@angular/forms";
import { GraphConstants } from "src/app/models/graph/Graph.constants";
import { ConvertDisplayLabelsPipe } from "src/app/pipes/convert-display-labels.pipe";
import { csvHelper } from "src/app/helpers/exporter/csvHelper";

@Component({
  selector: 'app-user-update',
  templateUrl: './user-update.component.html',
  styleUrls: ['./user-update.component.css'],
  providers: [ConvertDisplayLabelsPipe]
})
export class UserUpdateComponent implements OnInit {
  @Input() displayedColumns: string[] = [];
  @Input() userType: UserType | undefined;
  @Input() displayColumns: string[] = [];
  @Input() route: string;
  readonly UserType = UserType;

  //for toggle use
  readonly adminRole: string = 'support';
  readonly userTypesThatSupportUsingAdminRole: UserType[] = [UserType.Service, UserType.Guest];
  adminToggleOptions: string[];
  defaultRole: string = 'steward';
  showAdminToggleOptions: boolean = false;
  usingAdminRole: boolean = false;

  @ViewChild(MatPaginator, { static: false })
  set paginator(value: MatPaginator) {
    this.usersDataSource.paginator = value;
  }
  @ViewChild(MatSort, { static: false })
  set sort(value: MatSort) {
    this.usersDataSource.sort = value;
  }

  usersToUpdate: IUserUpdate[];
  userTypeString: string | undefined;
  selectedDisplayCols: string[];
  cols: string[]
  PageLayout = PageLayout;
  errorMessage: string;
  gettingUsers: boolean = true;
  isSubmitting: boolean = false;
  submittedRequests: IWorkflowRequest[] = [];
  submitErrorMessage = { message: '', errors: [] };
  selection = new SelectionModel<IUserUpdate>(true, []);
  retireResults: string[] = [];
  usersDataSource = new MatTableDataSource<IDirectoryUser>([]);
  usersDataSourceAsSteward = new MatTableDataSource<IDirectoryUser>([]);
  usersDataSourceAsSupport = new MatTableDataSource<IDirectoryUser>([]);
  updateUserDisplayedColumns: string[] = [];
  showSelectAllCheckbox: boolean = false;
  hasAdminRole: boolean = false;
  justification = new UntypedFormControl('', { validators: [Validators.required] });

  alertOnpremUserTypes: UserType[] = [UserType.Service, UserType.Privilege];

  constructor(
    private readonly convertDisplayLabelsPipe: ConvertDisplayLabelsPipe,
    private readonly userService: GraphUserService,
    private readonly formService: FormService,
    private readonly authService: CalAngularService) {
  }

  ngOnInit(): void {
    this.cols = ['select', ...this.displayColumns];
    this.selectedDisplayCols = [...this.displayColumns];
    this.updateUserDisplayedColumns = [...this.displayColumns];
    this.usersDataSource.paginator = this.paginator
    this.usersDataSource.sort = this.sort
    this.showSelectAllCheckbox = this.userType !== UserType.Coco;
    if (this.userType === UserType.Guest) {
      this.defaultRole = "sponsor";
    }

    this.adminToggleOptions = [this.defaultRole, this.adminRole]
    this.showAdminToggleOptions = this.userTypesThatSupportUsingAdminRole.some(x => x == this.userType);

    // check if the user has NonPrimaryUser.Manage.All? if so show search all section on UI
    this.hasAdminRole = this.userService.CheckIsUserNonPrimaryUserAdmin(this.authService.cvxClaimsPrincipal);
    this.search();
  }

  public onUserInfoChange(usersToUpdate: IUserUpdate[]) {
    this.submitErrorMessage = { message: '', errors: [] };
    this.usersToUpdate = usersToUpdate;
  }
  public onRecordDeleted(usersToDelete: IUserUpdate) {
    this.usersToUpdate = this.usersToUpdate.filter(user => user.id !== usersToDelete.id);
  }

  public select(row: IDirectoryUser) {
    this.selection.toggle(row);
  }

  public filter = (value: any = '') => {
    const filterValue = typeof value === 'string' ? value : value.target.value;
    this.usersDataSource.filter = filterValue.trim().toLocaleLowerCase();
  }

  public exportCocoUsersToCsv() {
    const columns = [
      'displayName',
      'mail',
      'employeeId',
      'department',
      'firstName',
      'lastName',
      'title',
      'stationNumber',
      'telephoneNumber',
      'mobilePhone',
      'cocoWorkdayEmployeeId'
    ];
    const cocoUsers = this.usersDataSource.data.map(user => ({
      displayName: user.displayName,
      mail: user.mail,
      employeeId: user.employeeId,
      department: user.department,
      firstName: user.firstName,
      lastName: user.lastName,
      title: user.jobTitle,
      stationNumber: user.department?.replace('Station ', ''),
      telephoneNumber: user.businessPhone,
      mobilePhone: user.mobilePhone,
      cocoWorkdayEmployeeId: user.employeeId
    }));
    const csvData = csvHelper.ConvertToCSV(cocoUsers, columns);
    csvHelper.DownloadFile(csvData, `coco-users-${new Date().toISOString()}`);
  }

  public onUpdateClick() {

    if (this.usersToUpdate.some(user => !user.isValid || (this.usingAdminRole && !this.justification.valid))) {
      this.formService.setIsValidateUpdateUserForms(true);
      return;
    }
    this.updateUsers(this.getUpdateRequest());
  }

  public get isServiceAccountUpdate() {
    return this.userType === UserType.Service
  }

  private getUpdateRequest() {
    switch (this.userType) {
      case UserType.Guest:
        return this.getUpdateGuestRequest();
      case UserType.Coco:
        return this.getUpdateCocoRequest();
      case UserType.Service:
        return this.getUpdateServiceAccountRequest();
    }
    return undefined;
  }

  private getUpdateCocoRequest(): UpdateCocoUserRequest {
    const filteredUsers = this.usersToUpdate.filter((user) => {
      if (user.updateUserInfo) {
        const cocoUser = user.updateUserInfo as CocoUser;
        return cocoUser.givenName || cocoUser.surname || cocoUser.telephoneNumber || cocoUser.jobTitle || cocoUser.mobilePhone || cocoUser.stationNumber || cocoUser.employeeId || cocoUser.stewardUserId;
      }
      return false;
    });
    const cocoUsers: CocoUser[] = filteredUsers.map(user => {
      const udpatedCocoUser = user.updateUserInfo as CocoUser;
      const stewardUserId = this.getStewardId(udpatedCocoUser.stewardUserId);
      return {
        userId: user.id,
        givenName: udpatedCocoUser.givenName ?? "",
        surname: udpatedCocoUser.surname ?? "",
        jobTitle: udpatedCocoUser.jobTitle ?? "None",
        telephoneNumber: udpatedCocoUser.telephoneNumber ?? "",
        mobilePhone: udpatedCocoUser.mobilePhone ?? "",
        stationNumber: udpatedCocoUser.stationNumber ?? "",
        employeeId: udpatedCocoUser.employeeId?.trim() ?? "",
        stewardUserId
      }
    });
    return new UpdateCocoUserRequest(cocoUsers);
  }

  private getUpdateGuestRequest(): UpdateGuestUserRequest {
    const filteredUsers = this.usersToUpdate.filter((user) => {
      if (user.updateUserInfo) {
        const guestUser = user.updateUserInfo as GuestUser;
        return guestUser.givenName || guestUser.stewardUserId || guestUser.surname;
      }
      return false;
    });
    const guestUsers: GuestUser[] = filteredUsers.map((user) => {
      const udpatedGuestUser = user.updateUserInfo as GuestUser;
      const stewardUserId = this.getStewardId(udpatedGuestUser.stewardUserId);
      const guestUser: GuestUser = {
        userId: user.id,
        givenName: udpatedGuestUser.givenName ?? "",
        surname: udpatedGuestUser.surname ?? "",
        stewardUserId
      }
      return guestUser;
    });
    return new UpdateGuestUserRequest(guestUsers, this.usingAdminRole, this.justification.value);
  }

  private getUpdateServiceAccountRequest(): UpdateServiceAccountRequest {
    const filteredUsers = this.usersToUpdate.filter((user) => {
      if (user.updateUserInfo) {
        const serviceAccount = user.updateUserInfo as ServiceAccount;
        return serviceAccount.applicationId || serviceAccount.stewardUserId;
      }
      return false;
    });
    const serviceAccounts: ServiceAccount[] = filteredUsers.map((user) => {
      const udpatedServiceAccount = user.updateUserInfo as ServiceAccount;
      const stewardUserId = this.getStewardId(udpatedServiceAccount.stewardUserId);
      const serviceAccount: ServiceAccount = {
        userId: user.id,
        applicationId: udpatedServiceAccount.applicationId ?? "",
        stewardUserId,
        emailId: udpatedServiceAccount.emailId
      }
      return serviceAccount;
    });
    return new UpdateServiceAccountRequest(serviceAccounts, this.usingAdminRole, this.justification.value);
  }

  private getStewardId(stewardUserId: string | undefined) {
    if (stewardUserId && stewardUserId !== "")
      return stewardUserId
    return undefined;
  }

  private updateUsers(updateUserRequest: UpdateGuestUserRequest | UpdateCocoUserRequest | UpdateServiceAccountRequest | undefined) {
    if (!updateUserRequest) return;
    this.isSubmitting = true;
    const updateObserver = {
      next: (x: IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>) => {
        this.submitErrorMessage = { message: '', errors: [] };
        this.submittedRequests = x.requests;
      },
      error: (err: any) => {
        this.submitErrorMessage.message = err.statusText;
        this.submitErrorMessage.errors = err?.error?.errors ?? [];
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    };
    switch (this.userType) {
      case UserType.Guest:
        this.userService.UpdateGuests(updateUserRequest as UpdateGuestUserRequest).subscribe(updateObserver);
        break;
      case UserType.Coco:
        this.userService.UpdateCocoUsers(updateUserRequest as UpdateCocoUserRequest).subscribe(updateObserver);
        break;
      case UserType.Service:
        this.userService.UpdateSevricesAccounts(updateUserRequest as UpdateServiceAccountRequest).subscribe(updateObserver);
        break;
    }
  }

  private hasPermissions(): boolean {
    if (this.userType == UserType.Coco) {
      return this.userService.CheckCocoUserSponsorshipPermission(this.authService.cvxClaimsPrincipal);
    }
    return true;
  }

  //for checkbox select all use
  isAllSelected() {
    return matTableSelectionHelper.isAllSelected(this.selection, this.usersDataSource);
  }
  masterToggle() {
    return matTableSelectionHelper.toggleSelectAll(this.selection, this.usersDataSource);
  }


  search() {
    this.userTypeString = this.userType?.toLowerCase();

    if (!this.userType) {
      this.errorMessage = 'Woops, looks like this component was used without providing a user type.';
      this.gettingUsers = false;
      return;
    }

    if (this.hasPermissions() || this.hasAdminRole) {
      this.selection.clear();
      this.gettingUsers = true;

      const term: string = "";
      let stewardId: string | undefined = '';
      let enabledAccountsOnly: boolean = true;

      if (this.usingAdminRole) {
        if (this.userType == UserType.Service)
          enabledAccountsOnly = false;
      } else {
        stewardId = this.userType === UserType.Coco ? undefined : this.authService.cvxClaimsPrincipal.objectId;
      }

      //when enabledAccountsOnly=false, search all enabeld and disabled users
      this.userService.SearchUser(GraphConstants.DefaultGraphDomainString, term, this.userType, stewardId, ResultSizeConstants.GetAll, enabledAccountsOnly).subscribe({
        next: (r) => {
          if (this.usingAdminRole) {
            this.usersDataSourceAsSupport.data = r.data;
          } else {
            this.usersDataSourceAsSteward.data = r.data;
          }
          this.usersDataSource.data = r.data;
        },
        error: (e) => {
          console.error(e);
          this.errorMessage = `Encountered unexpected error while searching for ${this.userTypeString} accounts.`;
          this.gettingUsers = false;
        },
        complete: () => {
          this.errorMessage = this.usersDataSource.data.length === 0 ? `No ${this.userTypeString} accounts were found.` : "";
          this.gettingUsers = false;
        }
      });
    } else {
      this.errorMessage = `Only authorized users can manage ${this.userTypeString} accounts.`;
      this.gettingUsers = false;
    }
  }

  get isGuestUser() {
    return this.userType == UserType.Guest;
  }

  public transformColumnHeading(colName: string) {
    if (this.isGuestUser && colName === "stewards") {
      return this.convertDisplayLabelsPipe.transform("sponsor");
    }
    return this.convertDisplayLabelsPipe.transform(colName);
  }

  isArrayWithElements(value: any): boolean {
    return Array.isArray(value) && value.length > 0;
  }

  handleToggleChange(selectedOption: string) {
    this.usingAdminRole = selectedOption === this.adminRole;
    // start fresh
    this.selection.clear();
    this.filter('');

    if (this.usingAdminRole) {
      if (this.usersDataSourceAsSupport.data.length == 0) {
        this.search();
      }
      this.cols = ['select', ...this.displayColumns, 'stewards', 'accountEnabled'];
      this.selectedDisplayCols = [...this.displayColumns, 'stewards', 'accountEnabled'];
      this.usersDataSource.data = this.usersDataSourceAsSupport.data;
    } else {
      this.cols = ['select', ...this.displayColumns];
      this.selectedDisplayCols = [...this.displayColumns];
      this.usersDataSource.data = this.usersDataSourceAsSteward.data
    }
  }
}